import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ListRenderItem, Platform, useWindowDimensions } from "react-native";
import { useDebounce } from "use-debounce";

import {
  IDialCodePickerProps,
  TDialCodeItem,
} from "@/features/auth/types/dialCodePickerTypes";
import DialCodePickerView from "./DialCodePickerView";

import CustomSelect from "@/components/molecules/CustomSelect";
import { SelectItem } from "@/components/atoms/Select";
import { searchIncludes } from "@/utils/normalizeForSearch";

const ITEM_PER_PAGE = 20;
const ITEM_HEIGHT = 48;

/**
 * Container component for dial code picker that provides country code selection
 * @param root0 - Component props
 * @param root0.data - Array of country dial code objects containing code and country name
 * @param root0.placeholder - Placeholder text displayed when no dial code is selected
 * @param root0.onChangeValue - Callback fired when a dial code is selected with the selected code value
 * @param root0.defaultValue - Initial dial code value to display on component mount
 */
const DialCodePickerContainer = ({
  data,
  placeholder,
  onChangeValue,
  defaultValue,
}: IDialCodePickerProps) => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    undefined
  );
  const [searchValue] = useDebounce(searchQuery, 200);
  const { height: windowHeight } = useWindowDimensions();

  // The picker's list area needs a concrete height: its actionsheet parent
  // sizes to content, so a percentage max-height plus flex-1 is a circular
  // constraint on Android — the list collapses/hangs and the full-screen
  // backdrop keeps eating touches (Cesena production login outage,
  // 2026-08-03). 80% of the window restores the original 80vh sizing.
  const listAreaStyle = useMemo(
    () => ({ height: windowHeight * 0.8 }),
    [windowHeight]
  );
  const options = useMemo(() => {
    return data.map(item => ({
      label: item.code,
      value: item.code,
    }));
  }, [data]);

  const filteredOptions = useMemo(() => {
    const filtered = data.filter(option =>
      searchIncludes(option.country, searchValue)
    );
    // Show all filtered results when searching, paginate only when not searching
    return searchValue ? filtered : filtered.slice(0, ITEM_PER_PAGE * page);
  }, [data, searchValue, page]);

  const handleChangeSelect = useCallback(
    (value: string) => {
      setSelectedValue(value);
      onChangeValue(value);
    },
    [onChangeValue]
  );

  const getItemLayout = useCallback(
    (_: ArrayLike<unknown> | null | undefined, index: number) => ({
      length: ITEM_HEIGHT,
      offset: ITEM_HEIGHT * index,
      index,
    }),
    []
  );

  const renderItem: ListRenderItem<TDialCodeItem> = useCallback(({ item }) => {
    return (
      <SelectItem
        label={`${item.country} (${item.code})`}
        value={item.code}
        className="h-12 font-body"
      />
    );
  }, []);

  const handleEndReached = useCallback(() => {
    if (searchQuery) return;
    if (ITEM_PER_PAGE * page < data.length) {
      setPage(prevPage => prevPage + 1);
    }
  }, [page, searchQuery, data]);

  const handleResetSearchInput = useCallback(() => {
    setSearchQuery("");
  }, []);

  useEffect(() => {
    // component did mount or data change
    // eslint-disable-next-line react-hooks/set-state-in-effect -- ANTI-PATTERN: Resetting state on prop change. FIX: Use key={dataId} on component to reset via remount
    setPage(1);

    setSearchQuery("");
  }, [data]);

  return Platform.OS === "web" ? (
    <CustomSelect
      options={options}
      defaultValue={defaultValue}
      placeholder="Code"
      onChangeValue={onChangeValue}
    />
  ) : (
    <DialCodePickerView
      options={filteredOptions}
      selectedValue={selectedValue || defaultValue}
      defaultValue={defaultValue}
      placeholder={placeholder}
      searchQuery={searchQuery}
      renderItem={renderItem}
      onChange={handleChangeSelect}
      onEndReached={handleEndReached}
      onSearchChange={setSearchQuery}
      getItemLayout={getItemLayout}
      onSearchInputReset={handleResetSearchInput}
      listAreaStyle={listAreaStyle}
    />
  );
};

export default memo(DialCodePickerContainer);
